import { useEffect, useRef, useState } from 'react';

import { Box, Card, Chip, Stack, TextField, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import BottomSheet from '@/designs-dev/device/BottomSheet';
import OptionCard from '@/components/wrapped/OptionCard';
import {
  ARRANGEMENT_OPTIONS,
  CIRCLE_DESCRIPTION,
  CONDITIONS_QUICK_ADD,
  FORK_OPTIONS,
  FORK_QUESTION,
  HELP_OPTIONS,
  HOURS_OPTIONS,
  ONBOARDING_BAR,
  POSTCODE_HELPER,
  QUESTIONS,
  RECIPIENT_NAME,
  STEP_META,
  STEP_ORDER,
  TOTAL_STEPS,
  type OnboardingMode,
} from '@/karehero-app/onboarding/Onboarding.content';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import CareCircle from '@/karehero-app/onboarding/CareCircle';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';

const noop = () => {};
const SCREEN_PX = 3; // 24px gutter — matches the sign-up screens

const ProgressBar = ({ n, label }: { n: number; label: string }) => (
  <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.25}>
    <Box
      sx={{
        flex: 1,
        height: 6,
        borderRadius: '3px',
        bgcolor: 'custom.neutral.200',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${(n / TOTAL_STEPS) * 100}%`,
          bgcolor: 'primary.main',
          borderRadius: '3px',
          transition: 'width 300ms ease',
        }}
      />
    </Box>
    <Typography variant='caption' color='text.secondary'>
      Step {label}/{TOTAL_STEPS}
    </Typography>
  </Stack>
);

// Two answers, stacked, and the same OptionCard every other answer list uses.
//
// It was a side-by-side pair with the label centred and no radio, on the
// grounds that a two-way choice does not need a control to tick. Two answers
// are not a different kind of question from three, though: read down the flow
// the row broke the rhythm, and it taught that a short answer set gets its own
// shape, which is the sort of rule that spreads. Stacked, it is one list.
const YesNo = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (v: 'yes' | 'no') => void;
}) => (
  <Stack spacing={1.5}>
    {(['yes', 'no'] as const).map((v) => (
      <OptionCard
        key={v}
        control='radio'
        selected={value === v}
        onClick={() => onChange(v)}
        label={v === 'yes' ? 'Yes' : 'No'}
      />
    ))}
  </Stack>
);

const QuickAddChips = ({
  items,
  addTitle = 'Add your own',
  placeholder,
}: {
  items: string[];
  addTitle?: string;
  placeholder?: string;
}) => {
  const [picked, setPicked] = useState<string[]>([]);
  // Chips the user typed themselves via '+ Add'. They join the row and start
  // selected, and toggle like any other chip.
  const [custom, setCustom] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus only once the sheet has finished sliding in. Autofocusing on mount
  // makes the browser scroll the phone screen to chase the input while it's
  // still translated off-screen, which lurches the whole screen.
  useEffect(() => {
    if (!adding) return;
    const t = window.setTimeout(
      () => inputRef.current?.focus({ preventScroll: true }),
      300,
    );
    return () => window.clearTimeout(t);
  }, [adding]);

  // Adds and keeps the sheet open, so several can go in one after another —
  // each new chip appears behind the sheet as it's added.
  const confirmAdd = () => {
    const value = draft.trim();
    if (value && ![...items, ...custom].includes(value)) {
      setCustom((c) => [...c, value]);
      setPicked((p) => [...p, value]);
    }
    setDraft('');
  };

  return (
    <Stack spacing={1}>
      <Typography variant='caption' color='text.secondary'>
        Quick add (optional)
      </Typography>
      <Stack
        sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
        direction='row'
      >
        {[...items, ...custom].map((c) => {
          const on = picked.includes(c);
          return (
            <Chip
              key={c}
              label={c}
              color={on ? 'limeGreen' : 'default'}
              variant={on ? 'filled' : 'outlined'}
              onClick={() =>
                setPicked((p) => (on ? p.filter((x) => x !== c) : [...p, c]))
              }
            />
          );
        })}
        <Chip
          label='+ Add'
          variant='outlined'
          onClick={() => setAdding(true)}
        />
      </Stack>

      {/* Non-modal sheet: no dim layer, so the chips behind stay visible and
          tappable while adding. */}
      {adding && (
        <BottomSheet
          title={addTitle}
          variant='hug'
          backdrop={false}
          onClose={() => setAdding(false)}
          footer={
            <Button
              variant='primary'
              size='lg'
              isFullWidth
              ariaLabel='Add'
              onPress={confirmAdd}
            >
              Add
            </Button>
          }
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmAdd();
              if (e.key === 'Escape') setAdding(false);
            }}
          />
        </BottomSheet>
      )}
    </Stack>
  );
};

// Hidden for now while the voice-input concept is parked — flip this to bring
// the 'Tell me instead' affordance back on every step.
const SHOW_TELL_ME_INSTEAD = false;

const MicHelper = () =>
  !SHOW_TELL_ME_INSTEAD ? null : (
    <Stack
      sx={{ alignItems: 'center', justifyContent: 'center' }}
      direction='row'
      spacing={1.25}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          border: 1,
          borderColor: 'custom.neutral.300',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Icon icon={'message' as never} fontSize={16} />
      </Box>
      <Typography variant='body2' color='text.secondary'>
        Tell me instead
      </Typography>
    </Stack>
  );

const SkipLink = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <Box onClick={onClick} sx={{ textAlign: 'center', cursor: 'pointer' }}>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{ textDecoration: 'underline' }}
    >
      {label}
    </Typography>
  </Box>
);

// Stacked large buttons — primary (Next) on top, secondary outline (Back) below.
const NavButtons = ({
  primaryLabel,
  onPrimary,
  onBack,
}: {
  primaryLabel: string;
  onPrimary: () => void;
  onBack: () => void;
}) => (
  <Stack spacing={2}>
    <Button
      variant='primary'
      size='lg'
      isFullWidth
      ariaLabel={primaryLabel}
      onPress={onPrimary}
    >
      {primaryLabel}
    </Button>
    <Button
      variant='secondary'
      size='lg'
      isFullWidth
      ariaLabel='Back'
      onPress={onBack}
    >
      Back
    </Button>
  </Stack>
);

const Onboarding = ({
  onComplete,
  onExit,
  initialStep = 0,
  initialMode = 'carer',
  initialAnswers = {},
  initialOpenMember = null,
  initialInviteOpen = false,
  account,
}: {
  onComplete: () => void;
  onExit?: () => void;
  /**
   * Draw the app bar, with the avatar that opens the account sheet. These
   * questions come after the account exists, so Help and Sign out have to stay
   * reachable: there is no Home behind them yet to carry them.
   */
  account?: Account;
  // Jump-state for handover minis: start on a given step (index into the
  // step order) with answers, mode and sheets already in place.
  initialStep?: number;
  initialMode?: OnboardingMode;
  initialAnswers?: Record<string, string>;
  initialOpenMember?: number | null;
  initialInviteOpen?: boolean;
}) => {
  const [step, setStep] = useState(initialStep);
  // Which fork path we're on: questions are the same, phrasing differs.
  const [mode, setMode] = useState<OnboardingMode>(initialMode);
  // The fork option picked but not yet confirmed with Next.
  const [forkPick, setForkPick] = useState<string | null>(null);
  const [a, setA] = useState<Record<string, string>>(initialAnswers);
  const [intent, setIntent] = useState<string[]>([]);

  const order = STEP_ORDER;
  const idx = Math.min(step, order.length - 1);
  const key = order[idx];

  const back = () => (idx === 0 ? onExit?.() : setStep(idx - 1));
  const next = () =>
    idx >= order.length - 1 ? onComplete() : setStep(idx + 1);
  const setRadio = (label: string) => setA((p) => ({ ...p, [key]: label }));
  const toggleIntent = (label: string) =>
    setIntent((p) =>
      p.includes(label) ? p.filter((x) => x !== label) : [...p, label],
    );
  const question = QUESTIONS[mode];

  const radio = (options: string[]) => (
    <Stack spacing={1.5}>
      {options.map((o) => (
        <OptionCard
          key={o}
          control='radio'
          selected={a[key] === o}
          onClick={() => setRadio(o)}
          label={o}
        />
      ))}
    </Stack>
  );

  // body: progress bar, then PAGE_HEADER_TOP space, then the left-aligned PageHeader + answer.
  const shell = (answer: React.ReactNode, sub?: string) => (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: SCREEN_PX,
          pt: 2,
          pb: 2,
        }}
      >
        <ProgressBar n={STEP_META[key].n} label={STEP_META[key].label} />
        <Box sx={{ pt: PAGE_HEADER_TOP }}>
          <Stack spacing={3}>
            <PageHeader title={question[key]} description={sub} variant='h2' />
            {answer}
            <MicHelper />
          </Stack>
        </Box>
      </Box>
      <Box
        sx={{
          px: SCREEN_PX,
          pt: 1.5,
          pb: 0.5,
          bgcolor: 'background.default',
          borderTop: 1,
          borderColor: 'custom.neutral.200',
        }}
      >
        <NavButtons primaryLabel='Next' onPrimary={next} onBack={back} />
      </Box>
    </>
  );

  const fieldProps = {
    fullWidth: true,
    slotProps: { inputLabel: { shrink: true } },
  } as const;

  let content: React.ReactNode;
  if (key === 'fork') {
    // Triage entry fork — no progress bar yet, but the same pick-then-Next
    // rhythm as every other step. Only the active-carer and self answers
    // lead into the steps; explorers skip the gate on Next.
    const submitFork = () => {
      const option = FORK_OPTIONS.find((o) => o.label === forkPick);
      if (!option) return;
      if (!option.mode) {
        onComplete();
        return;
      }
      setMode(option.mode);
      next();
    };
    content = (
      <>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: SCREEN_PX,
            pt: 2,
            pb: 2,
          }}
        >
          <Box sx={{ pt: PAGE_HEADER_TOP }}>
            <Stack spacing={3}>
              <PageHeader title={FORK_QUESTION} variant='h2' />
              <Stack spacing={1.5}>
                {FORK_OPTIONS.map((option) => (
                  <OptionCard
                    key={option.label}
                    control='radio'
                    selected={forkPick === option.label}
                    onClick={() => setForkPick(option.label)}
                    label={option.label}
                  />
                ))}
              </Stack>
              <MicHelper />
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            px: SCREEN_PX,
            pt: 1.5,
            pb: 0.5,
            bgcolor: 'background.default',
            borderTop: 1,
            borderColor: 'custom.neutral.200',
          }}
        >
          <NavButtons
            primaryLabel='Next'
            onPrimary={submitFork}
            onBack={back}
          />
        </Box>
      </>
    );
  } else if (key === '1') {
    content = shell(
      mode === 'self' ? (
        // No name field: Kinde registration already collected it.
        <TextField label='Date of birth' {...fieldProps} />
      ) : (
        <Stack spacing={2}>
          <TextField
            label='Name'
            defaultValue={RECIPIENT_NAME}
            {...fieldProps}
          />
          <TextField label='Date of birth' {...fieldProps} />
          <TextField
            label='Relationship to you'
            defaultValue='Mother'
            {...fieldProps}
          />
        </Stack>
      ),
    );
  } else if (key === '2') {
    content = shell(
      <Stack spacing={2}>
        <YesNo
          value={a['2']}
          onChange={(v) => setA((p) => ({ ...p, '2': v }))}
        />
        {a['2'] === 'yes' && <QuickAddChips {...CONDITIONS_QUICK_ADD} />}
      </Stack>,
    );
  } else if (key === '4') {
    content = shell(radio(ARRANGEMENT_OPTIONS));
  } else if (key === '4a') {
    // Only asked when there's no arrangement in place — that's when we'll be
    // searching for care nearby.
    content = shell(
      <TextField
        label='Postcode'
        placeholder='e.g. EH3 6HR'
        defaultValue=''
        {...fieldProps}
      />,
      POSTCODE_HELPER,
    );
  } else if (key === '6') {
    content = shell(radio(HOURS_OPTIONS));
  } else if (key === '7') {
    content = shell(
      <Stack spacing={1.5}>
        {HELP_OPTIONS.map((o) => (
          <OptionCard
            key={o}
            control='check'
            selected={intent.includes(o)}
            onClick={() => toggleIntent(o)}
            label={o}
          />
        ))}
      </Stack>,
      'Choose all that apply.',
    );
  } else {
    // Step 8 · care circle invite
    content = (
      <>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: SCREEN_PX,
            pt: 2,
            pb: 2,
          }}
        >
          <ProgressBar n={TOTAL_STEPS} label={String(TOTAL_STEPS)} />
          <Box sx={{ pt: PAGE_HEADER_TOP }}>
            <Stack spacing={3}>
              <PageHeader
                title='Finish setting up your care circle'
                description={CIRCLE_DESCRIPTION[mode]}
                variant='h2'
              />

              {/* The circle itself, shared with the intake, which asks the
                  same question while the reading happens. See CareCircle. */}
              <CareCircle
                initialOpenMember={initialOpenMember}
                initialInviteOpen={initialInviteOpen}
              />

              <SkipLink label='Do this later' onClick={onComplete} />
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            px: SCREEN_PX,
            pt: 1.5,
            pb: 0.5,
            bgcolor: 'background.default',
            borderTop: 1,
            borderColor: 'custom.neutral.200',
          }}
        >
          <NavButtons
            primaryLabel='Done'
            onPrimary={onComplete}
            onBack={back}
          />
        </Box>
      </>
    );
  }

  return (
    <ScreenRoot relative>
      <StatusBar />
      {account && <AccountBar {...account} label={ONBOARDING_BAR} />}
      {content}
      <HomeIndicator />
    </ScreenRoot>
  );
};

export default Onboarding;
